import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Camera, KeyRound, Loader2, Save } from "lucide-react";
import { supabase } from "../../../supabaseClient.jsx";
import { validateEmail, validateName, validatePhone } from "../../../utils/validators.js";
import ImageCropperModal from "../../../components/ImageCropperModal.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { uploadToStorage } from "../../../utils/uploadToStorage";

// Colunas do perfil do admin retornadas/editadas na tabela `usuarios`
const PROFILE_COLUMNS =
  "id, nome, sobrenome, email, telefone, cargo, email_recuperacao, foto_url, bio, updated_at";

// Bucket público onde a foto do admin é armazenada (mesmo padrão do restante do app)
const PHOTO_BUCKET = import.meta.env.VITE_STORAGE_DEFAULT_BUCKET || "cadastros";

const initialForm = {
  nome: "",
  sobrenome: "",
  email: "",
  telefone: "",
  cargo: "",
  email_recuperacao: "",
  bio: "",
  foto_url: "",
};

const sanitizePhone = (value) => (value || "").replace(/\D/g, "");

// foto_url pode estar como URL absoluta (legado) ou como path relativo no bucket.
// Resolve para uma URL pública exibível.
const resolvePhotoUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const { data } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(value);
  return data?.publicUrl || "";
};

const validateStrongPassword = (password) => {
  if (typeof password !== "string") return "Senha inválida.";
  if (password.length < 10) return "A senha deve ter no mínimo 10 caracteres.";
  if (!/[A-Z]/.test(password)) return "A senha deve conter ao menos 1 letra maiúscula.";
  if (!/[a-z]/.test(password)) return "A senha deve conter ao menos 1 letra minúscula.";
  if (!/[0-9]/.test(password)) return "A senha deve conter ao menos 1 número.";
  if (!/[^A-Za-z0-9]/.test(password)) return "A senha deve conter ao menos 1 caractere especial.";
  return null;
};

// Registro de auditoria best-effort — nunca bloqueia o fluxo se a RLS negar.
const auditAdminAction = async ({ action, userId, metadata = {} }) => {
  try {
    await supabase.from("admin_user_actions").insert({
      action,
      target_user_id: userId,
      performed_by: userId,
      metadata,
    });
  } catch {
    // auditoria é opcional; ignora falhas silenciosamente
  }
};

async function getAdminUserId() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  let userId = session?.user?.id ?? null;

  if (!userId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    userId = user?.id ?? null;
  }

  if (!userId) {
    throw new Error("Sessão inválida. Faça login novamente.");
  }
  return userId;
}

export default function AdminProfileSection() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const initials = useMemo(() => {
    const full = `${form.nome || ""} ${form.sobrenome || ""}`.trim();
    return (full || "Admin").slice(0, 2).toUpperCase();
  }, [form.nome, form.sobrenome]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const userId = await getAdminUserId();

      const { data, error } = await supabase
        .from("usuarios")
        .select(PROFILE_COLUMNS)
        .eq("id", userId)
        .single();

      if (error) throw new Error(error.message || "Falha ao carregar perfil.");
      if (!data) throw new Error("Perfil não encontrado.");

      setForm((prev) => ({
        ...prev,
        ...data,
        foto_url: resolvePhotoUrl(data.foto_url),
      }));
    } catch (err) {
      toast.error(err.message || "Erro ao carregar perfil do administrador.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!validateName(form.nome || "")) {
      throw new Error("Nome inválido. Informe ao menos 2 caracteres válidos.");
    }

    if (!validateName(form.sobrenome || "")) {
      throw new Error("Sobrenome inválido. Informe ao menos 2 caracteres válidos.");
    }

    if (!validateEmail(form.email || "")) {
      throw new Error("E-mail de contato inválido.");
    }

    if (form.email_recuperacao && !validateEmail(form.email_recuperacao)) {
      throw new Error("E-mail de recuperação inválido.");
    }

    const phoneResult = validatePhone(form.telefone || "");
    if (!phoneResult?.valid) {
      throw new Error(phoneResult?.msg || "Telefone inválido.");
    }
  };

  const handleSave = async () => {
    try {
      validateForm();
      setSaving(true);

      const userId = await getAdminUserId();

      // foto_url é gerenciado pelo fluxo de upload da foto — não incluído aqui
      const payload = {
        nome: (form.nome || "").trim(),
        sobrenome: (form.sobrenome || "").trim(),
        email: (form.email || "").trim().toLowerCase(),
        telefone: sanitizePhone(form.telefone),
        cargo: (form.cargo || "").trim(),
        email_recuperacao: (form.email_recuperacao || "").trim().toLowerCase(),
        bio: (form.bio || "").trim(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("usuarios")
        .update(payload)
        .eq("id", userId)
        .select(PROFILE_COLUMNS)
        .single();

      if (error) throw new Error(error.message || "Falha ao salvar perfil.");

      setForm((prev) => ({
        ...prev,
        ...data,
        foto_url: resolvePhotoUrl(data.foto_url),
      }));

      auditAdminAction({ action: "update_profile", userId });
      toast.success("Perfil atualizado com sucesso.");
    } catch (err) {
      toast.error(err.message || "Não foi possível salvar o perfil.");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      if (!newPassword || !confirmPassword) {
        throw new Error("Preencha e confirme a nova senha.");
      }

      if (newPassword !== confirmPassword) {
        throw new Error("A confirmação da senha não confere.");
      }

      const passwordError = validateStrongPassword(newPassword);
      if (passwordError) throw new Error(passwordError);

      setPasswordSaving(true);

      const userId = await getAdminUserId();
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw new Error(error.message || "Falha ao atualizar senha.");

      auditAdminAction({ action: "change_password", userId });

      setNewPassword("");
      setConfirmPassword("");
      toast.success("Senha atualizada com sucesso.");
    } catch (err) {
      toast.error(err.message || "Erro ao atualizar senha.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSelectAvatar = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Formato inválido. Use JPG, PNG ou WEBP.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Limite de 2MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setCropperSrc(objectUrl);
    setCropperOpen(true);
  };

  const handleCropClose = () => {
    if (cropperSrc?.startsWith("blob:")) {
      URL.revokeObjectURL(cropperSrc);
    }
    setCropperOpen(false);
    setCropperSrc(null);
    setSelectedFile(null);
  };

  const handleAvatarUpload = async (croppedBlob) => {
    try {
      if (!croppedBlob) return;

      const userId = await getAdminUserId();

      const file = new File([croppedBlob], `${Date.now()}.webp`, {
        type: croppedBlob.type || "image/webp",
      });

      // Upload direto no Storage (bucket `cadastros`, path `${userId}/admin-avatar/...`).
      // O admin escreve no próprio prefixo, então a RLS base já permite.
      const { path } = await uploadToStorage(file, "admin-avatar", { userId });

      const { error: updateError } = await supabase
        .from("usuarios")
        .update({ foto_url: path, updated_at: new Date().toISOString() })
        .eq("id", userId);

      if (updateError) throw new Error(updateError.message || "Falha ao salvar a foto.");

      auditAdminAction({ action: "update_avatar", userId, metadata: { path } });

      setForm((prev) => ({ ...prev, foto_url: resolvePhotoUrl(path) }));
      toast.success("Foto de perfil atualizada.");
      handleCropClose();
    } catch (err) {
      toast.error(err.message || "Erro ao enviar foto de perfil.");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando perfil do administrador...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Perfil do Administrador</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="size-20">
              <AvatarImage src={form.foto_url || ""} alt="Avatar administrador" />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleSelectAvatar}
                />
                <span className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-input text-sm cursor-pointer hover:bg-muted transition-colors">
                  <Camera className="size-4" />
                  Atualizar foto
                </span>
              </label>
              <p className="text-xs text-muted-foreground">
                Formatos: JPG, PNG, WEBP. Tamanho máximo: 2MB. Recomendado: 400x400+.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="admin-nome">Nome</Label>
              <Input
                id="admin-nome"
                value={form.nome || ""}
                onChange={(e) => updateField("nome", e.target.value)}
                placeholder="Nome"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-sobrenome">Sobrenome</Label>
              <Input
                id="admin-sobrenome"
                value={form.sobrenome || ""}
                onChange={(e) => updateField("sobrenome", e.target.value)}
                placeholder="Sobrenome"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-email">E-mail de contato</Label>
              <Input
                id="admin-email"
                type="email"
                value={form.email || ""}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="admin@dominio.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-telefone">Telefone</Label>
              <Input
                id="admin-telefone"
                value={form.telefone || ""}
                onChange={(e) => updateField("telefone", e.target.value)}
                placeholder="(21) 99999-9999"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-cargo">Cargo</Label>
              <Input
                id="admin-cargo"
                value={form.cargo || ""}
                onChange={(e) => updateField("cargo", e.target.value)}
                placeholder="Administrador"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-email-recuperacao">E-mail de recuperação</Label>
              <Input
                id="admin-email-recuperacao"
                type="email"
                value={form.email_recuperacao || ""}
                onChange={(e) => updateField("email_recuperacao", e.target.value)}
                placeholder="recuperacao@dominio.com"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-bio">Bio</Label>
            <textarea
              id="admin-bio"
              className="w-full min-h-24 rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              value={form.bio || ""}
              onChange={(e) => updateField("bio", e.target.value)}
              placeholder="Resumo profissional do administrador"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Segurança da Conta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="admin-new-password">Nova senha</Label>
              <Input
                id="admin-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nova senha"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-confirm-password">Confirmar nova senha</Label>
              <Input
                id="admin-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmar nova senha"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={handleChangePassword} disabled={passwordSaving}>
              {passwordSaving ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
              {passwordSaving ? "Atualizando..." : "Atualizar senha"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {cropperOpen && cropperSrc && (
        <ImageCropperModal
          imageSrc={cropperSrc}
          aspect={1}
          onCropComplete={handleAvatarUpload}
          onClose={handleCropClose}
        />
      )}
    </div>
  );
}